import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Barcode from 'react-barcode';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const DocketLabelPrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const printRef = useRef(null);

  const [printData, setPrintData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Read query params if opened from the modal
  const queryParams = new URLSearchParams(location.search);
  const qFrom = queryParams.get('from');
  const qTo = queryParams.get('to');
  const qPkgs = parseInt(queryParams.get('packages'), 10);
  const qAdditional = queryParams.get('additional');
  const qSize = queryParams.get('size') || '4x6';

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    const fetchPrintData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${backendUrl}/api/dockets/${id}/print-data`, {
          headers: getAuthHeaders()
        });
        if (!res.ok) throw new Error('Failed to load docket data');
        const data = await res.json();
        setPrintData(data);
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchPrintData();
  }, [id, backendUrl, toast]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center print:hidden">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (!printData) {
    return (
      <div className="min-h-screen flex items-center justify-center print:hidden">
        <p className="text-gray-500">Docket details not found.</p>
      </div>
    );
  }

  const { docket, company, resolved_shipment } = printData;
  const payload = docket.payload_json || {};
  const header = payload.header || {};
  const shipmentInfo = payload.shipment_info || {};
  const pkgDetails = payload.package_details || payload.package_info || payload.cargo_details || {};

  const docketNo = docket.printed_docket_no || docket.system_docket_no || '000000';
  const cleanBarcodeVal = docketNo.replace(/[^A-Z0-9\-_]/gi, '');

  // Detect whether this is Freight Forwarding mode (AIR / SEA / INTL)
  const isFreightForwarding = 
    docket.shipment_type === 'AIR' ||
    docket.shipment_type === 'SEA' ||
    ['INTL_AIR_V1', 'INTL_SEA_V1', 'AIR_V2'].includes(docket.schema_id) ||
    (company?.business_modes && company.business_modes.includes('FREIGHT_FORWARDING'));

  // Common location and package resolutions
  const fromLocation = qFrom || resolved_shipment?.loading_code || resolved_shipment?.loading_from || header.from_location_name || 'ORIGIN';
  const toLocation = qTo || resolved_shipment?.discharge_code || resolved_shipment?.discharge_at || resolved_shipment?.delivery_code || resolved_shipment?.delivery_at || header.to_location_name || 'DESTINATION';
  
  // Package calculation
  let calcPackages = 0;
  if (Array.isArray(payload.invoices) && payload.invoices.length > 0) {
    calcPackages = payload.invoices.reduce((acc, inv) => acc + (parseInt(inv.no_of_packages, 10) || 0), 0);
  }
  const totalPackages = qPkgs || calcPackages || parseInt(pkgDetails.package_no_of_packages || pkgDetails.no_of_packages || payload.packages || 1, 10) || 1;

  // Weight calculation
  let calcWeight = '';
  if (Array.isArray(payload.invoices) && payload.invoices.length > 0) {
    const sumGross = payload.invoices.reduce((acc, inv) => acc + (parseFloat(inv.gross_weight) || 0), 0);
    if (sumGross > 0) calcWeight = `${sumGross.toFixed(sumGross % 1 === 0 ? 0 : 2)} kg`;
  }
  if (!calcWeight) {
    const grossVal = pkgDetails.package_gross_weight || pkgDetails.gross_weight || payload.gross_weight;
    if (grossVal) calcWeight = `${grossVal} kg`;
  }

  // Airline / Carrier Name
  const airlineName = resolved_shipment?.carrier || shipmentInfo.airline || shipmentInfo.carrier_name || (docket.shipment_type === 'SEA' ? 'SHIPPING LINE' : 'AIRLINE');

  // Air Waybill Number (MAWB or Docket No)
  const awbNo = shipmentInfo.mawb_no || docketNo;

  // House Air Waybill Number (HAWB No)
  const hawbNo = shipmentInfo.hawb_no || payload.house_awb_no || '';

  // Barcode value for Air Waybill
  // If airline prefix (e.g. '098') exists and mawb_no exists, create standard cargo barcode: [prefix][mawb_no][piece_index_padded]
  const carrierPrefix = resolved_shipment?.carrier_prefix_code || (awbNo.includes('-') ? awbNo.split('-')[0] : '');
  const cleanMawb = awbNo.replace(/[^A-Z0-9]/gi, '');

  const additionalInfo = qAdditional || '';
  const is4x2 = qSize === '4x2';

  // Company full info line
  const companyAddressLine = [
    company?.address,
    company?.city,
    company?.state,
    company?.country ? `${company.country} - ${company.pincode || ''}` : company?.pincode
  ].filter(Boolean).join(', ');

  // Generate an array of piece items: [1, 2, ..., totalPackages]
  const pieces = Array.from({ length: Math.max(1, totalPackages) }, (_, i) => i + 1);

  return (
    <div className="bg-gray-100 min-h-screen py-6 print:py-0 print:bg-white font-sans text-black">
      {/* Top action toolbar (hidden when printing) */}
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between print:hidden">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => {
            if (window.opener) {
              window.close();
            } else {
              navigate(`/dockets/edit/${id}`);
            }
          }} 
          className="gap-1.5 font-ibm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Docket
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600 font-ibm">
            Mode: <strong>{isFreightForwarding ? 'Freight Forwarding' : 'Logistics'}</strong> | Format: <strong>{is4x2 ? '4 × 2 in' : '4 × 6 in'}</strong> ({totalPackages} {totalPackages > 1 ? 'Labels' : 'Label'})
          </span>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 text-white font-ibm gap-1.5 cursor-pointer">
            <Printer className="w-4 h-4" /> Print Labels
          </Button>
        </div>
      </div>

      {/* Embedded thermal printing styles */}
      <style>{`
        @media print {
          @page {
            size: ${is4x2 ? '4in 2in' : '4in 6in'};
            margin: 0;
          }
          body {
            margin: 0;
            padding: 0;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .thermal-page {
            page-break-after: always;
            break-after: page;
            margin: 0 auto !important;
            box-shadow: none !important;
            border: 2px solid black !important;
          }
        }
      `}</style>

      {/* Pages Container */}
      <div ref={printRef} className="flex flex-col items-center gap-6 print:gap-0">
        {pieces.map((pieceNum) => {
          // Format piece barcode string (e.g., 0983197713100001 or clean barcode)
          const paddedPiece = String(pieceNum).padStart(4, '0');
          const airCargoBarcodeVal = cleanMawb ? `${cleanMawb}${paddedPiece}` : `${cleanBarcodeVal}${paddedPiece}`;

          return (
            <div
              key={pieceNum}
              className={`thermal-page bg-white border-2 border-black flex flex-col justify-between box-border p-0 mx-auto overflow-hidden ${
                is4x2 ? 'w-[4in] h-[2in]' : 'w-[4in] h-[6in]'
              }`}
              style={{ width: '4in', height: is4x2 ? '2in' : '6in', boxSizing: 'border-box' }}
            >
              {isFreightForwarding ? (
                /* ================= FREIGHT FORWARDING MODE (AIR WAYBILL CARGO LABEL) ================= */
                is4x2 ? (
                  /* --- 4 × 2 Compact Air Cargo Label --- */
                  <div className="h-full flex flex-col justify-between text-[11px] leading-tight p-2">
                    {/* Airline & AWB Header */}
                    <div className="border-b border-black pb-1 flex items-center justify-between">
                      <div className="font-black text-xs uppercase tracking-wide">
                        {airlineName}
                      </div>
                      <div className="text-[10px] font-mono font-bold">
                        AWB: {awbNo}
                      </div>
                    </div>

                    {/* Barcode & Number */}
                    <div className="flex flex-col items-center justify-center my-0.5">
                      <Barcode
                        value={airCargoBarcodeVal}
                        format="CODE128"
                        width={1.5}
                        height={26}
                        displayValue={false}
                        margin={0}
                      />
                      <div className="text-[10px] font-mono font-bold tracking-wider">{airCargoBarcodeVal}</div>
                    </div>

                    {/* Route & Piece */}
                    <div className="border-t border-black pt-1 grid grid-cols-3 text-[10px] items-center">
                      <div>
                        <span className="font-bold">ORG:</span> <span className="uppercase font-semibold">{fromLocation}</span>
                      </div>
                      <div className="text-center">
                        <span className="font-bold">DEST:</span> <span className="uppercase font-semibold">{toLocation}</span>
                      </div>
                      <div className="text-right font-bold font-mono">
                        {pieceNum}/{totalPackages}
                      </div>
                    </div>

                    {/* Compact Company & Address Footer */}
                    <div className="border-t border-black pt-0.5 text-[8.5px] leading-tight truncate text-gray-800">
                      <span className="font-bold text-black uppercase">{company?.name || 'STAVAN INTERNATIONAL LLP'}</span>
                      {companyAddressLine && ` • ${companyAddressLine}`}
                      {company?.gst_number && ` • GST: ${company.gst_number}`}
                    </div>
                  </div>
                ) : (
                  /* --- 4 × 6 Standard Air Waybill Cargo Label (Matches Mockup media_1791620903783_bb2cec9e.png) --- */
                  <div className="h-full flex flex-col justify-between text-xs leading-tight">
                    {/* SECTION 1: AIRLINE HEADER */}
                    <div className="border-b-2 border-black p-2 relative text-center min-h-[0.65in] flex items-center justify-center">
                      <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                        Airline
                      </span>
                      <h1 className="text-xl font-black uppercase tracking-wider text-black mt-1">
                        {airlineName}
                      </h1>
                    </div>

                    {/* SECTION 2: BARCODE SECTION */}
                    <div className="border-b-2 border-black py-3 flex flex-col items-center justify-center min-h-[1.1in]">
                      <Barcode
                        value={airCargoBarcodeVal}
                        format="CODE128"
                        width={2.2}
                        height={55}
                        displayValue={false}
                        margin={0}
                      />
                      <div className="text-xs font-mono font-bold tracking-widest mt-1.5">
                        {airCargoBarcodeVal}
                      </div>
                    </div>

                    {/* SECTION 3: AIR WAYBILL NO. */}
                    <div className="border-b-2 border-black p-2 relative text-center min-h-[0.65in] flex items-center justify-center">
                      <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                        Air Waybill No.
                      </span>
                      <div className="text-xl font-black font-mono tracking-widest text-black mt-1">
                        {awbNo}
                      </div>
                    </div>

                    {/* SECTION 4: DESTINATION & TOTAL NO. OF PIECES */}
                    <div className="border-b-2 border-black grid grid-cols-2 divide-x-2 divide-black min-h-[0.75in]">
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          Destination
                        </span>
                        <div className="text-base font-black uppercase text-black tracking-wide leading-tight break-words">
                          {toLocation}
                        </div>
                      </div>
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          Total No. of Pieces
                        </span>
                        <div className="text-2xl font-black text-black">
                          {totalPackages}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 5: WEIGHT OF CONSIGNMENT & ORIGIN */}
                    <div className="border-b-2 border-black grid grid-cols-2 divide-x-2 divide-black min-h-[0.75in]">
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          Weight of Consignment
                        </span>
                        <div className="text-xl font-black text-black">
                          {calcWeight || '—'}
                        </div>
                      </div>
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          Origin
                        </span>
                        <div className="text-base font-black uppercase text-black tracking-wide leading-tight break-words">
                          {fromLocation}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 6: HAWB NO. & PIECE NO. */}
                    <div className="border-b-2 border-black grid grid-cols-2 divide-x-2 divide-black min-h-[0.75in]">
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          HAWB No.
                        </span>
                        <div className="text-xl font-black font-mono text-black uppercase tracking-wider truncate">
                          {hawbNo || '—'}
                        </div>
                      </div>
                      <div className="p-2 relative flex flex-col justify-end">
                        <span className="absolute top-1 left-2 text-[9px] font-bold text-gray-700 uppercase tracking-wider">
                          Piece No.
                        </span>
                        <div className="text-2xl font-black text-black">
                          {pieceNum}/{totalPackages}
                        </div>
                      </div>
                    </div>

                    {/* SECTION 7: ADDITIONAL INFORMATION (FULL 1 COLUMN) */}
                    <div className="p-2.5 flex flex-col justify-between min-h-[1.1in] flex-1">
                      <span className="text-[9px] font-bold text-gray-700 uppercase tracking-wider block mb-1">
                        Additional Information
                      </span>
                      <div className="flex-1 overflow-hidden space-y-1.5">
                        {additionalInfo && (
                          <p className="text-[10px] font-semibold text-black line-clamp-2">
                            {additionalInfo}
                          </p>
                        )}
                        <div className="text-[8.5px] leading-snug text-gray-900 border-t border-gray-200 pt-1">
                          <span className="font-bold text-black block uppercase text-[9.5px]">
                            {company?.name || 'STAVAN INTERNATIONAL LLP'}
                          </span>
                          <p className="line-clamp-2 text-gray-700 mt-0.5">
                            {companyAddressLine || company?.address || ''}
                          </p>
                          <div className="flex flex-wrap gap-x-3 mt-1 text-gray-600 font-medium text-[9px]">
                            {company?.phone && <span>Tel: {company.phone}</span>}
                            {company?.email && <span>Email: {company.email}</span>}
                            {company?.gst_number && <span className="font-bold text-black">GST: {company.gst_number}</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SECTION 8: BOTTOM FOOTER BAR */}
                    <div className="py-1 px-3 text-[10px] font-semibold tracking-wide text-black bg-white flex items-center justify-between border-t-2 border-black">
                      <span className="font-mono text-black">www.stavan.co.in</span>
                      <span className="font-mono font-bold text-black">AWB - {awbNo}</span>
                    </div>
                  </div>
                )
              ) : (
                /* ================= LOGISTICS MODE (ROAD / RAIL / DOMESTIC) ================= */
                is4x2 ? (
                  /* 4 × 2 COMPACT LOGISTICS LABEL */
                  <div className="h-full flex flex-col justify-between text-[11px] leading-tight">
                    {/* Header */}
                    <div className="border-b border-black pb-1 flex items-center justify-between">
                      <div className="font-bold text-xs truncate max-w-[2.2in]">
                        {company?.name || 'STAVAN LOGISTICS PRIVATE LIMITED'}
                      </div>
                      <div className="text-[10px] font-mono font-bold">
                        Piece: {pieceNum}/{totalPackages}
                      </div>
                    </div>

                    {/* Barcode & Number */}
                    <div className="flex flex-col items-center justify-center my-0.5">
                      <Barcode
                        value={cleanBarcodeVal}
                        format="CODE128"
                        width={1.6}
                        height={28}
                        displayValue={false}
                        margin={0}
                      />
                      <div className="text-[11px] font-mono font-bold tracking-wider">{docketNo}</div>
                    </div>

                    {/* Route & Info */}
                    <div className="border-t border-black pt-1 grid grid-cols-2 text-[10px]">
                      <div>
                        <span className="font-bold">FROM:</span> <span className="uppercase">{fromLocation}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">TO:</span> <span className="uppercase">{toLocation}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* 4 × 6 STANDARD LOGISTICS SHIPPING LABEL */
                  <div className="h-full flex flex-col justify-between text-xs leading-tight p-2.5">
                    {/* COMPANY HEADER BLOCK */}
                    <div className="border-b-2 border-black pb-2 flex items-start gap-2">
                      {/* Left: Brand Logo */}
                      <div className="w-[1.2in] shrink-0 flex flex-col items-center justify-center pt-1">
                        {company?.logo_url ? (
                          <img
                            src={company.logo_url}
                            alt="Logo"
                            className="max-h-14 max-w-full object-contain"
                          />
                        ) : (
                          <div className="text-center">
                            <span className="text-xl font-black tracking-tight text-blue-900 block font-sans">
                              STAVAN
                            </span>
                            <span className="text-[8px] italic text-gray-700 block -mt-1 font-serif">
                              Execution of Commitments
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Right: Company Registered Info */}
                      <div className="flex-1 text-[9px] leading-tight text-gray-900 pr-1">
                        <div className="font-bold text-[10px] uppercase text-black leading-snug">
                          {company?.name || 'STAVAN LOGISTICS PRIVATE LIMITED'}
                        </div>
                        <div className="mt-0.5 text-gray-800 line-clamp-2">
                          {companyAddressLine || company?.address || '7th Floor, 737-738, Sun Gravitas, Radio Mirchi Road, Vejalpur, Ahmedabad 380015, Gujarat, India.'}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-2">
                          {company?.email && <span>{company.email}</span>}
                          {company?.gst_number && <span className="font-semibold">GST - {company.gst_number}</span>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-2">
                          {company?.phone && <span>(O) {company.phone}</span>}
                          {company?.phone_secondary && <span>(M) {company.phone_secondary}</span>}
                        </div>
                        {company?.website && (
                          <div className="text-[8.5px] text-gray-700">
                            (W) {company.website.replace(/^https?:\/\//, '')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* BARCODE SECTION */}
                    <div className="border-b-2 border-black py-2.5 flex flex-col items-center justify-center">
                      <Barcode
                        value={cleanBarcodeVal}
                        format="CODE128"
                        width={2.1}
                        height={46}
                        displayValue={false}
                        margin={0}
                      />
                      <div className="text-xs font-mono font-bold tracking-widest mt-1">
                        {docketNo}
                      </div>
                    </div>

                    {/* ROUTE BLOCK: From / To */}
                    <div className="border-b-2 border-black grid grid-cols-2 divide-x-2 divide-black min-h-[0.9in]">
                      <div className="p-1.5 flex flex-col justify-start">
                        <span className="text-[10px] font-bold uppercase tracking-wider block">From</span>
                        <span className="text-xs font-bold uppercase mt-1 break-words">{fromLocation}</span>
                      </div>
                      <div className="p-1.5 flex flex-col justify-start">
                        <span className="text-[10px] font-bold uppercase tracking-wider block">To</span>
                        <span className="text-xs font-bold uppercase mt-1 break-words">{toLocation}</span>
                      </div>
                    </div>

                    {/* NO. OF PACKAGES */}
                    <div className="border-b-2 border-black p-1.5 min-h-[0.55in] flex flex-col justify-center">
                      <span className="text-[10px] font-bold uppercase tracking-wider block">No. of Packages</span>
                      <span className="text-sm font-bold mt-0.5">{totalPackages}</span>
                    </div>

                    {/* ADDITIONAL INFO & PIECE NUMBER */}
                    <div className="grid grid-cols-2 divide-x-2 divide-black min-h-[1.1in] flex-1">
                      <div className="p-1.5 flex flex-col justify-between">
                        <div>
                          <span className="text-[10px] font-bold uppercase tracking-wider block">Additional Information</span>
                          <p className="text-[10px] mt-1 text-gray-800 whitespace-pre-wrap break-words line-clamp-4">
                            {additionalInfo || '—'}
                          </p>
                        </div>
                      </div>
                      <div className="p-1.5 flex flex-col items-center justify-center">
                        <span className="text-[10px] font-bold uppercase tracking-wider block mb-2">Piece No.</span>
                        <span className="text-lg font-black tracking-wider">
                          {pieceNum}/{totalPackages}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DocketLabelPrint;
